import { GAME_WEIGHTS, SOFTWARE_OPTIONS, GAME_OPTIONS } from "@/lib/apps-games-catalog";

// Some answers rule an Apple laptop out before the visitor ever gets to the OS
// and brand questions. Two cases:
//
//   1. Graphically intensive games. Apple Silicon has no discrete GPU option and
//      almost none of these titles ship a Mac build (or run under anti-cheat).
//   2. Engineering software that is Windows/Linux only, or that only runs on a
//      Mac through a VM. Recommending a MacBook to someone who has to run
//      SolidWorks would be actively wrong.
//
// When either applies, the questionnaire drops "apple" from the brand answer,
// drops a "mac" OS preference, disables both options, and explains why.

/**
 * 3DMark Time Spy graphics score at which we call a game "graphically
 * intensive". The catalog's tiers are roughly 3.6k (esports/mid), 5.9k, 7.5k and
 * 9.1k (AAA), so this cuts in at the first genuinely demanding tier and keeps
 * classifying new games as the catalog grows.
 */
export const GPU_INTENSIVE_TIME_SPY_G = 5000;

/**
 * Engineering software with no native macOS build (value → why). Anything in the
 * Engineering category that isn't listed here does run natively on a Mac:
 * MATLAB/Simulink, Fusion 360, COMSOL, KiCad, LTspice and Excel/Python.
 */
export const MAC_INCOMPATIBLE_SOFTWARE: Record<string, string> = {
  solidworks: "Windows only",
  "ansys-workbench": "Windows and Linux only",
  abaqus: "Windows and Linux only",
  "autodesk-revit": "Windows only",
  "autodesk-inventor": "Windows only",
  catia: "Windows only",
  "siemens-nx": "Windows and Linux only",
  "ptc-creo": "Windows only",
  openfoam: "Linux-native; on a Mac it only runs inside Docker or a VM",
  "aspen-plus": "Windows only",
  "aspen-hysys": "Windows only",
  gproms: "Windows only",
  labview: "no current macOS release",
  "ni-multisim": "Windows only",
  "orcad-pspice": "Windows only",
  "altium-designer": "Windows only",
  "cadence-virtuoso": "Linux only",
  "autocad-civil-3d": "Windows only",
  "tekla-structures": "Windows only",
  "sap2000-etabs": "Windows only",
  "autodesk-robot-lusas": "Windows only",
  plaxis: "Windows only",
  simapro: "Windows only",
  "fpga-vivado": "Windows and Linux only",
};

export function isGpuIntensiveGame(value: string): boolean {
  const score = GAME_WEIGHTS[value]?.three_d_mark_time_spy_g;
  return score !== undefined && score >= GPU_INTENSIVE_TIME_SPY_G;
}

export type MacBlockers = {
  /** Labels of the selected games that are too graphically demanding for a Mac. */
  games: string[];
  /** Selected software that won't run natively on macOS, with the reason. */
  software: { label: string; reason: string }[];
};

const labelOf = (options: { value: string; label: string }[], value: string) =>
  options.find((o) => o.value === value)?.label ?? value;

/** What, if anything, in these answers rules out an Apple laptop. */
export function getMacBlockers(answers: Record<string, unknown>): MacBlockers {
  const games = (answers["games"] as string[] | undefined) ?? [];
  const software = (answers["software"] as string[] | undefined) ?? [];

  return {
    games: games.filter(isGpuIntensiveGame).map((v) => labelOf(GAME_OPTIONS, v)),
    software: software
      .filter((v) => v in MAC_INCOMPATIBLE_SOFTWARE)
      .map((v) => ({ label: labelOf(SOFTWARE_OPTIONS, v), reason: MAC_INCOMPATIBLE_SOFTWARE[v] })),
  };
}

export const hasMacBlockers = (b: MacBlockers) => b.games.length > 0 || b.software.length > 0;

const listOut = (items: string[]) =>
  items.length <= 2 ? items.join(" and ") : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;

/** The paragraph shown on the OS and brand questions when an Apple laptop is out. */
export function macBlockerMessage(b: MacBlockers): string {
  const reasons: string[] = [];

  if (b.games.length > 0) {
    reasons.push(
      `${listOut(b.games)} ${b.games.length === 1 ? "needs" : "need"} a dedicated graphics card to play at a decent frame rate, and no Apple laptop ships with one.`,
    );
  }

  if (b.software.length > 0) {
    const named = listOut(b.software.slice(0, 3).map((s) => `${s.label} (${s.reason})`));
    const extra = b.software.length > 3 ? `, plus ${b.software.length - 3} more you picked,` : "";
    reasons.push(`${named}${extra} ${b.software.length === 1 ? "does" : "do"} not run natively on macOS.`);
  }

  return `We've ruled out Apple laptops for you. ${reasons.join(" ")} You'd be paying a premium for a machine that can't do the thing you bought it for, so we'll only look at Windows laptops from here. If you'd rather keep Apple in the running, go back and change those picks.`;
}
